import {
  StationStatus,
  SessionStatus,
  TransactionStatus,
  UserStatus,
  UserRole,
  BadgeStyle,
} from '../enums/status';

// Generic status config interface
interface StatusConfig {
  style: BadgeStyle;
  label: string;
}

// Station Status Configuration
const STATION_STATUS_CONFIG: Record<StationStatus, StatusConfig> = {
  [StationStatus.ACTIVE]: { style: BadgeStyle.ACTIVE, label: 'Active' },
  [StationStatus.IN_USE]: { style: BadgeStyle.IN_USE, label: 'In-Use' },
  [StationStatus.INACTIVE]: { style: BadgeStyle.INACTIVE, label: 'Inactive' },
};

// Session Status Configuration
const SESSION_STATUS_CONFIG: Record<SessionStatus, StatusConfig> = {
  [SessionStatus.CHARGING]: { style: BadgeStyle.IN_USE, label: 'Charging' },
  [SessionStatus.PAUSED]: { style: BadgeStyle.PENDING, label: 'Paused' },
  [SessionStatus.CLOSED]: { style: BadgeStyle.INACTIVE, label: 'Closed' },
  [SessionStatus.FINISHED]: { style: BadgeStyle.ACTIVE, label: 'Finished' },
  [SessionStatus.PREPARE]: { style: BadgeStyle.INACTIVE, label: 'Preparing' },
  [SessionStatus.PENDING]: { style: BadgeStyle.PENDING, label: 'Pending' },
  [SessionStatus.CONFIRMED]: { style: BadgeStyle.ACTIVE, label: 'Confirmed' },
  [SessionStatus.CANCELLED]: { style: BadgeStyle.FAILURE, label: 'Cancelled' },
  [SessionStatus.UNAUTHORIZED]: { style: BadgeStyle.FAILURE, label: 'Unauthorized' },
};

// Transaction Status Configuration
const TRANSACTION_STATUS_CONFIG: Record<TransactionStatus, StatusConfig> = {
  [TransactionStatus.SUCCESS]: { style: BadgeStyle.ACTIVE, label: 'Success' },
  [TransactionStatus.PROCESSING]: { style: BadgeStyle.IN_USE, label: 'Processing' },
  [TransactionStatus.CREATED]: { style: BadgeStyle.INACTIVE, label: 'Created' },
  [TransactionStatus.FAILURE]: { style: BadgeStyle.FAILURE, label: 'Failed' },
  [TransactionStatus.REVERSED]: { style: BadgeStyle.REVERSED, label: 'Reversed' },
  [TransactionStatus.PENDING]: { style: BadgeStyle.PENDING, label: 'Pending' },
};

// User Status Configuration
const USER_STATUS_CONFIG: Record<UserStatus, StatusConfig> = {
  [UserStatus.ACTIVE]: { style: BadgeStyle.ACTIVE, label: 'Active' },
  [UserStatus.INACTIVE]: { style: BadgeStyle.INACTIVE, label: 'Inactive' },
  [UserStatus.BLOCKED]: { style: BadgeStyle.FAILURE, label: 'Blocked' },
};

// User Role Configuration
const USER_ROLE_CONFIG: Record<UserRole, { class: string; label: string }> = {
  [UserRole.ADMIN]: { class: 'role-badge-admin', label: 'Admin' },
  [UserRole.MANAGER]: { class: 'role-badge-manager', label: 'Manager' },
  [UserRole.USER]: { class: 'role-badge-user', label: 'User' },
};

// Generic helper functions
const getBadgeClass = (style: BadgeStyle): string => `status-badge-${style}`;
const getDotClass = (style: BadgeStyle): string => `status-dot-${style}`;
const getMobileBadgeClass = (style: BadgeStyle): string => `mobile-status-badge-${style}`;
const getMobileDotClass = (style: BadgeStyle): string => `mobile-status-dot-${style}`;

// Station Status Functions
export const getStationStatusBadgeClass = (status: string): string => {
  const config = STATION_STATUS_CONFIG[status as StationStatus];
  return getBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getStationStatusDotClass = (status: string): string => {
  const config = STATION_STATUS_CONFIG[status as StationStatus];
  return getDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileStationStatusBadgeClass = (status: string): string => {
  const config = STATION_STATUS_CONFIG[status as StationStatus];
  return getMobileBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileStationStatusDotClass = (status: string): string => {
  const config = STATION_STATUS_CONFIG[status as StationStatus];
  return getMobileDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const formatStationStatus = (status: string): string => {
  const config = STATION_STATUS_CONFIG[status as StationStatus];
  return config?.label ?? status;
};

// Session Status Functions
export const getSessionStatusBadgeClass = (status: string): string => {
  const config = SESSION_STATUS_CONFIG[status as SessionStatus];
  return getBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getSessionStatusDotClass = (status: string): string => {
  const config = SESSION_STATUS_CONFIG[status as SessionStatus];
  return getDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileSessionStatusBadgeClass = (status: string): string => {
  const config = SESSION_STATUS_CONFIG[status as SessionStatus];
  return getMobileBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileSessionStatusDotClass = (status: string): string => {
  const config = SESSION_STATUS_CONFIG[status as SessionStatus];
  return getMobileDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const formatSessionStatus = (status: string): string => {
  const config = SESSION_STATUS_CONFIG[status as SessionStatus];
  return config?.label ?? status;
};

// Transaction Status Functions
export const getTransactionStatusBadgeClass = (status: string): string => {
  const config = TRANSACTION_STATUS_CONFIG[status as TransactionStatus];
  return getBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getTransactionStatusDotClass = (status: string): string => {
  const config = TRANSACTION_STATUS_CONFIG[status as TransactionStatus];
  return getDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileTransactionStatusBadgeClass = (status: string): string => {
  const config = TRANSACTION_STATUS_CONFIG[status as TransactionStatus];
  return getMobileBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileTransactionStatusDotClass = (status: string): string => {
  const config = TRANSACTION_STATUS_CONFIG[status as TransactionStatus];
  return getMobileDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const formatTransactionStatus = (status: string): string => {
  const config = TRANSACTION_STATUS_CONFIG[status as TransactionStatus];
  return config?.label ?? status;
};

// User Status Functions
export const getUserStatusBadgeClass = (status: string): string => {
  const config = USER_STATUS_CONFIG[status as UserStatus];
  return getBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getUserStatusDotClass = (status: string): string => {
  const config = USER_STATUS_CONFIG[status as UserStatus];
  return getDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileUserStatusBadgeClass = (status: string): string => {
  const config = USER_STATUS_CONFIG[status as UserStatus];
  return getMobileBadgeClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const getMobileUserStatusDotClass = (status: string): string => {
  const config = USER_STATUS_CONFIG[status as UserStatus];
  return getMobileDotClass(config?.style ?? BadgeStyle.INACTIVE);
};

export const formatUserStatus = (status: string): string => {
  const config = USER_STATUS_CONFIG[status as UserStatus];
  return config?.label ?? status;
};

// User Role Functions
export const getRoleBadgeClass = (role: string): string => {
  const config = USER_ROLE_CONFIG[role as UserRole];
  return config?.class ?? 'role-badge-user';
};

export const formatUserRole = (role: string): string => {
  const config = USER_ROLE_CONFIG[role as UserRole];
  return config?.label ?? role;
};

// Legacy aliases for backward compatibility (Station)
export const getStatusBadgeClass = getStationStatusBadgeClass;
export const getStatusDotClass = getStationStatusDotClass;
export const getMobileStatusBadgeClass = getMobileStationStatusBadgeClass;
export const getMobileStatusDotClass = getMobileStationStatusDotClass;
export const formatStatus = formatStationStatus;
